import React, { useEffect, useRef, useState } from 'react';
import { AnalysisResponse } from '../types/analysis';
import ResultsDisplay from './ResultsDisplay';
import DemoExamples, { getCardIcon } from './DemoExamples';
import { analyzeText } from '../services/analysisService';

export type { AnalysisResponse };

const MAX_CHARS = 2_000;
const MIN_CHARS = 50;

const MainAnalyzer: React.FC = () => {
  const [analysisResult, setAnalysisResult] = useState<AnalysisResponse | null>(null);
  const [activeText, setActiveText] = useState<string>('');
  const [animKey, setAnimKey] = useState(0);
  const [fading, setFading] = useState(false);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeExampleId, setActiveExampleId] = useState<number | null>(null);
  const [activeExampleSeverity, setActiveExampleSeverity] = useState<string | null>(null);
  const [activeExampleTitle, setActiveExampleTitle] = useState<string | null>(null);
  const [loadingStage, setLoadingStage] = useState(0);
  const fadeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const analyzeAnchorRef = useRef<HTMLDivElement>(null);

  const LOADING_STAGES = [
    'Reading your text…',
    'Scanning for emotional tactics…',
    'Checking logical patterns…',
    'Assessing social pressure…',
    'Evaluating urgency cues…',
    'Building trust recommendation…',
    'Finalizing analysis…',
  ];

  useEffect(() => {
    if (!loading) { setLoadingStage(0); return; }
    const interval = setInterval(() => {
      setLoadingStage(s => Math.min(s + 1, LOADING_STAGES.length - 1));
    }, 2200);
    return () => clearInterval(interval);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const severityIconColor = (severity: string | null) => {
    switch (severity) {
      case 'red': return 'text-red-500';
      case 'orange': return 'text-orange-500';
      case 'yellow': return 'text-yellow-500';
      case 'green': return 'text-green-500';
      default: return 'text-gray-400';
    }
  };

  const severityBadgeClass = (severity: string | null) => {
    switch (severity) {
      case 'red': return 'bg-red-500 text-white';
      case 'orange': return 'bg-orange-500 text-white';
      case 'yellow': return 'bg-yellow-400 text-white';
      case 'green': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const showResult = (text: string, result: AnalysisResponse) => {
    if (fadeTimeout.current) clearTimeout(fadeTimeout.current);
    if (analysisResult) {
      setFading(true);
      fadeTimeout.current = setTimeout(() => {
        setActiveText(text);
        setAnalysisResult(result);
        setAnimKey(k => k + 1);
        setFading(false);
      }, 180);
    } else {
      setActiveText(text);
      setAnalysisResult(result);
      setAnimKey(k => k + 1);
    }
  };

  const handleExampleClick = (text: string, cachedResult: AnalysisResponse, exampleId: number, severity: string, title: string) => {
    setActiveExampleId(exampleId);
    setActiveExampleSeverity(severity);
    setActiveExampleTitle(title);
    showResult(text, cachedResult);
  };

  const handleClear = () => {
    setAnalysisResult(null);
    setActiveText('');
    setActiveExampleId(null);
    setActiveExampleSeverity(null);
    setActiveExampleTitle(null);
    requestAnimationFrame(() => {
      analyzeAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });
  };

  const handleSubmit = async () => {
    if (loading) return;
    setError(null);
    setLoading(true);
    setActiveExampleId(null);
    setActiveExampleSeverity(null);
    setActiveExampleTitle(null);
    try {
      const result = await analyzeText(inputText.trim());
      showResult(inputText.trim(), result);
      setInputText('');
    } catch (err: any) {
      setError(err.message || 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Demo Examples Section */}
      <DemoExamples onExampleClick={handleExampleClick} />

      {/* Divider */}
      <div className="relative py-2">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-200" />
        </div>
      </div>

      {/* Analyze your own text section */}
      <div>
        <div className="text-center mb-5">
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Analyze Your Own Text</h2>
          <p className="text-gray-500 text-sm">
            Paste any conversation, message, email, or ad copy to detect manipulation patterns
          </p>
        </div>

        {loading ? (
          <div className="py-6 text-center space-y-3">
            <div className="flex justify-center gap-1">
              {[0, 1, 2].map(i => (
                <span
                  key={i}
                  className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
            <p className="text-sm text-gray-500 transition-all duration-500">
              {LOADING_STAGES[loadingStage]}
            </p>
          </div>
        ) : analysisResult ? (
          <div className="text-center py-2">
            <button
              onClick={() => { handleClear(); setInputText(''); }}
              className="text-sm text-gray-500 hover:text-gray-900 font-medium underline underline-offset-2 transition-colors"
            >
              Try again →
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <textarea
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              placeholder="Paste a conversation, message, email, or ad copy here…"
              rows={6}
              disabled={loading}
              className="w-full px-4 py-3 text-sm border-2 border-gray-200 rounded-xl resize-none focus:outline-none focus:border-gray-400 text-gray-700 placeholder-gray-400 disabled:opacity-50 transition-colors"
            />
            <div className="flex items-center justify-between">
              <span
                className={`text-xs ${
                  inputText.length > 0 && (inputText.length < MIN_CHARS || inputText.length > MAX_CHARS)
                    ? 'text-red-400'
                    : 'text-gray-400'
                }`}
              >
                {inputText.length.toLocaleString()} / {MAX_CHARS.toLocaleString()} chars
                {inputText.length > 0 && inputText.length < MIN_CHARS && ` (min ${MIN_CHARS})`}
              </span>
              <button
                onClick={handleSubmit}
                disabled={loading || inputText.trim().length < MIN_CHARS || inputText.length > MAX_CHARS}
                className="px-6 py-2 text-sm font-semibold bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Analyzing…' : 'Analyze →'}
              </button>
            </div>
            {error && <p className="text-xs text-red-500">{error}</p>}
          </div>
        )}

        <p className="mt-3 text-xs text-gray-400 text-center">
          v1 is an API prototype. The goal is an open-source model — fine-tuned on labeled manipulation data and self-hostable by anyone.
        </p>
      </div>

      {/* Scroll anchor — closing results scrolls back here */}
      <div ref={analyzeAnchorRef} />

      {/* Results Display */}
      <div id="analyzer-results">
        {analysisResult && (
          <div
            className={`transition-opacity duration-150 ${fading ? 'opacity-0' : 'opacity-100'}`}
          >
            <div key={animKey} className="results-animate space-y-4">
              {/* Sticky close bar — stays at top while scrolling through results, disappears when section exits viewport */}
              <div className="sticky top-0 z-10 -mx-6 lg:-mx-8 px-6 lg:px-8 py-2.5 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  {activeExampleId ? (
                    <span className={`${severityIconColor(activeExampleSeverity)}`}>
                      {getCardIcon(activeExampleId, 'w-5 h-5 opacity-70')}
                    </span>
                  ) : (
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  )}
                  <span className="font-medium text-gray-700">{activeExampleTitle ?? 'Analysis Results'}</span>
                  {analysisResult && (
                    <span className={`text-sm font-bold ${severityIconColor(analysisResult.severity_level)}`}>
                      · {analysisResult.overall_score}/10
                    </span>
                  )}
                  {analysisResult && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${severityBadgeClass(analysisResult.severity_level)}`}>
                      {analysisResult.severity_level.toUpperCase()}
                    </span>
                  )}
                </div>
                <button
                  onClick={handleClear}
                  className="text-sm text-gray-400 hover:text-gray-700 font-semibold px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                >
                  × Close
                </button>
              </div>
              <ResultsDisplay result={analysisResult} originalText={activeText} onClose={handleClear} />
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default MainAnalyzer;
